/**
 * interviewScoreBands.js
 *
 * Shared CCRI/ARS band helpers, styles and breakdown bar component.
 * Single source of truth — imported by InstructorInterviewScoring,
 * CabinInterviewSessionDetail and InstructorInterviewReviews.
 *
 * IMPORTANT: Band threshold values are canonical (mirror backend logic).
 * Do NOT change thresholds without a matching backend update.
 */

import { motion } from 'framer-motion';

// ── CCRI band ─────────────────────────────────────────────────────────────────

export function getCCRIBandKey(score) {
  if (score >= 85) return 'band_ccri_very_high';
  if (score >= 70) return 'band_ccri_high';
  if (score >= 55) return 'band_ccri_mid';
  return 'band_ccri_low';
}

export const CCRI_BAND_STYLES = {
  band_ccri_very_high: { bg: '#D1FAE5', border: '#34D399', text: '#065F46' },
  band_ccri_high:      { bg: '#DCFCE7', border: '#4ADE80', text: '#14532D' },
  band_ccri_mid:       { bg: '#FEF9C3', border: '#FACC15', text: '#713F12' },
  band_ccri_low:       { bg: '#FEE2E2', border: '#F87171', text: '#7F1D1D' },
};

// ── ARS band ──────────────────────────────────────────────────────────────────

export function getARSBandKey(score) {
  if (score >= 90) return 'band_ars_outstanding';
  if (score >= 75) return 'band_ars_strong';
  if (score >= 60) return 'band_ars_adequate';
  if (score >= 40) return 'band_ars_developing';
  return 'band_ars_insufficient';
}

export const ARS_BAND_STYLES = {
  band_ars_outstanding:  { bg: '#ECFDF5', border: '#10B981', text: '#064E3B' },
  band_ars_strong:       { bg: '#F0FDF4', border: '#22C55E', text: '#14532D' },
  band_ars_adequate:     { bg: '#FEFCE8', border: '#EAB308', text: '#713F12' },
  band_ars_developing:   { bg: '#FFF7ED', border: '#F97316', text: '#7C2D12' },
  band_ars_insufficient: { bg: '#FFF1F2', border: '#F43F5E', text: '#881337' },
};

// ── Breakdown max values (canonical — mirrors backend computation) ─────────────

export const CCRI_BREAKDOWN_MAX = {
  fiziksel_sunum:    15,
  iletisim_diksiyon: 20,
  ingilizce:         15,
  motivasyon:        15,
  kriz:              15,
  ekip:              10,
  cv_kariyer:        10,
};

export const ARS_BREAKDOWN_MAX = {
  kamera_durusu:      15,
  cevap_kalitesi:     20,
  ozguven:            15,
  havayolu_kulturu:   15,
  ingilizce_cevap:    15,
  stres:              10,
  profesyonel_imaj:   10,
};

// ── Semantic score color ───────────────────────────────────────────────────────
/**
 * Returns a semantic color set based on a percentage score (0–100).
 *
 * Three bands — user-approved 3-tier percentage rule:
 *   high   (≥ 80) — emerald  "Çok İyi" / "Excellent"
 *   mid    (≥ 60) — amber    "Geliştirilmeli" / "Needs Improvement"
 *   low    (<  60) — red     "Yetersiz" / "Insufficient"
 *
 * WCAG-verified hex values (ui-ux spec):
 *   YEŞİL: bar #10B981, text #047857, bg #D1FAE5, border #10B981
 *   SARI:  bar #D97706, text #B45309, bg #FEF3C7, border #F59E0B
 *   KIRMIZI: bar #EF4444, text #DC2626, bg #FEE2E2, border #EF4444
 *
 * IMPORTANT: These thresholds govern VISUAL COLOR ONLY.
 * Do NOT use them for backend band computation — getCCRIBandKey / getARSBandKey
 * own that logic and must not be changed here.
 *
 * Validation: 70→mid (SARI), 72→mid (SARI), 12/15=80%→high (YEŞİL), 14/20=70%→mid (SARI), 7/10=70%→mid (SARI)
 *
 * @param {number} pct  — Percentage 0-100 (value / max * 100)
 * @returns {{ bar, text, bg, border, tailwindBar, tailwindText, tier, labelKey, areaLabelKey }}
 */
export function getScoreColor(pct) {
  const p = pct != null ? Math.max(0, Math.min(100, pct)) : 0;
  if (p >= 80) {
    return {
      bar:          '#10B981',  // emerald-500
      text:         '#047857',  // emerald-700
      bg:           '#D1FAE5',  // emerald-100
      border:       '#10B981',  // emerald-500
      tailwindBar:  'bg-emerald-500',
      tailwindText: 'text-emerald-700',
      tier:         'high',
      labelKey:     'interview.scoreTier.high.label',
      areaLabelKey: 'interview.scoreTier.high.areaLabel',
    };
  }
  if (p >= 60) {
    return {
      bar:          '#D97706',  // amber-600
      text:         '#B45309',  // amber-700
      bg:           '#FEF3C7',  // amber-100
      border:       '#F59E0B',  // amber-500
      tailwindBar:  'bg-amber-600',
      tailwindText: 'text-amber-700',
      tier:         'mid',
      labelKey:     'interview.scoreTier.mid.label',
      areaLabelKey: 'interview.scoreTier.mid.areaLabel',
    };
  }
  return {
    bar:          '#EF4444',  // red-500
    text:         '#DC2626',  // red-600
    bg:           '#FEE2E2',  // red-100
    border:       '#EF4444',  // red-500
    tailwindBar:  'bg-red-500',
    tailwindText: 'text-red-600',
    tier:         'low',
    labelKey:     'interview.scoreTier.low.label',
    areaLabelKey: 'interview.scoreTier.low.areaLabel',
  };
}

// ── BreakdownBar component ────────────────────────────────────────────────────
/**
 * Animated horizontal bar for CCRI/ARS sub-score display.
 * Bar color is driven by getScoreColor(pct) — semantic (low/mid/high),
 * NOT a fixed brand-primary blue.
 *
 * @param {string}  label    — Display label
 * @param {number}  value    — Raw score value
 * @param {number}  [max=100]
 * @param {boolean} [reduced=false] — Skip animation (prefers-reduced-motion)
 */
export function BreakdownBar({ label, value, max = 100, reduced = false }) {
  const safeVal = value != null ? value : 0;
  const pct = max > 0 ? Math.min(100, Math.round((safeVal / max) * 100)) : 0;
  const scoreColor = getScoreColor(pct);
  return (
    <div className="flex items-center gap-2">
      <span className="text-xs text-gray-600 w-36 shrink-0 truncate">{label}</span>
      <div className="flex-1 h-2 bg-gray-100 rounded-full overflow-hidden">
        <motion.div
          className={`h-full rounded-full ${scoreColor.tailwindBar}`}
          initial={reduced ? false : { width: 0 }}
          animate={{ width: `${pct}%` }}
          transition={{ duration: 0.5, ease: 'easeOut' }}
          style={{ width: `${pct}%` }}
        />
      </div>
      <span className={`text-xs font-bold w-14 text-right tabular-nums shrink-0 ${scoreColor.tailwindText}`}>
        {Math.round(safeVal)} / {max}
      </span>
    </div>
  );
}
